import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { type ApiError, isApiErrorResponse } from 'ts/common/common';
import { type IFetchHookResponse, useTranslate } from 'ts/common/hooks';
import { FetchMethod, useClientApiFetch } from 'client_react/bootstrap';
import { getQuestionnaireEmailStorageKey } from 'client_react/questionnaire/hooks/common';

// `Client_QuestionnaireSubmissionController` throws a `BadRequestException` for exactly two
// validation failures, both passed as `$validationErrors` — which `ApiErrorResponseException`
// puts under `info.errors` (an array), not directly under `info` (that always holds the same
// generic boilerplate: "There was a problem with your request. Please see `info` for more
// information."). There's no error code to key off, only this raw message text, so unfortunately
// that's what has to be matched here; anything unrecognized falls back to the generic message.
const SUBMIT_VALIDATION_MESSAGE_KEYS: Record<string, string> = {
    'Some required questions have no response.': 'submitMissingRequiredResponses',
    'This questionnaire has already been completed.': 'submitAlreadyCompleted'
};

export function getSubmitErrorDescription(
    response: IFetchHookResponse<null | ApiError>,
    t: (key: string) => string
): string {
    const info =
        response && typeof response === 'object' && 'info' in response
            ? (response as { info?: unknown }).info
            : undefined;
    const errors =
        info && typeof info === 'object' && 'errors' in info
            ? (info as { errors?: unknown }).errors
            : undefined;

    if (Array.isArray(errors) && errors.length > 0) {
        return errors
            .map((message) => {
                const key =
                    typeof message === 'string'
                        ? SUBMIT_VALIDATION_MESSAGE_KEYS[message]
                        : undefined;
                return key ? t(key) : String(message);
            })
            .join(' ');
    }

    return response?.detail ?? t('errorSavingQuestionnaire');
}

interface Props {
    email?: Nullable<string>;
    questionnairePublicId?: Nullable<string>;
}

export type QuestionnaireContext = {
    questionnaire?: Nullable<SpApi.Client.IQuestionnaireMinimal | SpApi.Client.IQuestionnaire>;
    fetchQuestionnaire: (
        email?: string
    ) => Promise<
        IFetchHookResponse<
            SpApi.Client.IQuestionnaireMinimal | SpApi.Client.IQuestionnaire | ApiError
        >
    >;
    loading: boolean;
    saveQuestionResponse: (
        questionId: number,
        responseText: string | null
    ) => Promise<IFetchHookResponse<null | ApiError>>;
    saving: boolean;
    submitQuestionnaire: (options?: {
        showSuccessToast?: boolean;
    }) => Promise<IFetchHookResponse<null | ApiError>>;
    submitting: boolean;
    requestFailed: boolean;
    error: Nullable<ApiError>;
};

const useQuestionnaire = ({
    email: existingEmail,
    questionnairePublicId
}: Props): QuestionnaireContext => {
    const t = useTranslate('clientQuestionnaire');
    const toast = useToast();

    const [error, setError] = useState<Nullable<ApiError>>(null);
    const [requestFailed, setRequestFailed] = useState(false);
    const [questionnaire, setQuestionnaire] = useState<
        SpApi.Client.IQuestionnaireMinimal | SpApi.Client.IQuestionnaire
    >();

    const { loading, performFetch } = useClientApiFetch<SpApi.Client.IQuestionnaire | ApiError>(
        `questionnaire/${questionnairePublicId}`,
        {
            defer: true,
            method: FetchMethod.GET
        }
    );

    const fetchQuestionnaire = useCallback(
        async (email?: Nullable<string>) => {
            if (!questionnairePublicId) {
                return null;
            }

            const response = await performFetch({
                search: email ? { email } : undefined
            });

            if (response && !isApiErrorResponse(response)) {
                setQuestionnaire(response);
                setError(null);
                setRequestFailed(false);

                if (email) {
                    const questionnaireEmailStorageKey =
                        getQuestionnaireEmailStorageKey(questionnairePublicId);
                    sessionStorage.setItem(questionnaireEmailStorageKey, email);
                }
            } else {
                setError(response);
                setRequestFailed(true);
            }

            return response;
        },
        [performFetch, questionnairePublicId]
    );

    useEffect(() => {
        if (!questionnairePublicId) {
            return;
        }

        const questionnaireEmailStorageKey = getQuestionnaireEmailStorageKey(questionnairePublicId);
        const email = existingEmail ?? sessionStorage.getItem(questionnaireEmailStorageKey);

        (async () => {
            const response = await fetchQuestionnaire(email);

            if (response?.status === 403) {
                sessionStorage.removeItem(questionnaireEmailStorageKey);
                await fetchQuestionnaire();
            }
        })();
    }, [existingEmail, fetchQuestionnaire, questionnairePublicId]);

    const { loading: saving, performFetch: patchQuestionnaire } = useClientApiFetch<unknown>(
        `questionnaire/${questionnairePublicId}`,
        {
            defer: true,
            method: FetchMethod.PATCH
        }
    );

    // `patchQuestionnaire` above is one shared `useFetch` instance for the whole page — it keeps
    // only a single options/doFetch/responseResolve slot, not one per call. A questionnaire page
    // can have many `QuestionnaireQuestionField`s saving independently (e.g. blurring a text field
    // right as the user clicks a radio option on the next question), and calling `performFetch`
    // again before a prior call's response arrives silently discards that prior call's response
    // handling — even though its request had already gone out over the network. Rather than change
    // the shared hook, every save here is funneled through this FIFO queue so `patchQuestionnaire`
    // never has two calls in flight at once: each save's request is only sent once the previous one
    // has fully settled, which keeps the shared hook's single-slot state safe and also preserves
    // the order in which the user made their edits.
    const saveQueueRef = useRef<Promise<unknown>>(Promise.resolve());

    const saveQuestionResponse = useCallback(
        async (questionId: number, responseText: string | null) => {
            if (!questionnairePublicId) {
                throw new Error('Questionnaire public ID is required to save a question response');
            }

            const questionnaireEmailStorageKey =
                getQuestionnaireEmailStorageKey(questionnairePublicId);
            const email = existingEmail ?? sessionStorage.getItem(questionnaireEmailStorageKey);

            const runSave = async () => {
                const response = await patchQuestionnaire({
                    data: { id: questionId, response_text: responseText },
                    search: email ? { email } : undefined
                });

                if (response && !isApiErrorResponse(response)) {
                    setError(null);
                    setRequestFailed(false);

                    setQuestionnaire((prev) => {
                        if (!prev || !('pages' in prev)) {
                            return prev;
                        }

                        return {
                            ...prev,
                            questionnaireStatus: 'partial',
                            pages: prev.pages.map((page) => ({
                                ...page,
                                questions: page.questions.map((q) =>
                                    q.id === questionId ? { ...q, responseText } : q
                                )
                            }))
                        };
                    });
                } else {
                    setError(response);
                    setRequestFailed(true);
                    toast({
                        title: t('error'),
                        description: response?.detail ?? t('errorSavingQuestionnaire'),
                        status: 'error'
                    });
                }

                return response;
            };

            // Chain behind whatever's already queued. The `.catch` on the *previous* link means a
            // failed earlier save doesn't wedge the queue — this save still runs on its turn.
            const queuedSave = saveQueueRef.current.catch(() => undefined).then(runSave);
            saveQueueRef.current = queuedSave;

            return queuedSave;
        },
        [existingEmail, patchQuestionnaire, questionnairePublicId, setQuestionnaire, toast, t]
    );

    const { loading: postSubmissionLoading, performFetch: postSubmission } =
        useClientApiFetch<unknown>(`questionnaire/${questionnairePublicId}/submission`, {
            defer: true,
            method: FetchMethod.POST
        });
    // Also covers the window below where we're waiting on the save queue, before the POST itself
    // even starts (postSubmissionLoading alone only reflects the POST's own in-flight state).
    const [isPreparingSubmit, setIsPreparingSubmit] = useState(false);
    const submitting = postSubmissionLoading || isPreparingSubmit;

    const submitQuestionnaire = useCallback(
        async ({ showSuccessToast = true }: { showSuccessToast?: boolean } = {}) => {
            if (!questionnairePublicId) {
                throw new Error('Questionnaire public ID is required to submit a questionnaire');
            }

            const questionnaireEmailStorageKey =
                getQuestionnaireEmailStorageKey(questionnairePublicId);
            const email = existingEmail ?? sessionStorage.getItem(questionnaireEmailStorageKey);

            if (!email) {
                toast({
                    title: t('error'),
                    description: t('emailHeading'),
                    status: 'error'
                });
                return null;
            }

            setIsPreparingSubmit(true);

            try {
                // Wait for every save enqueued so far to settle — including one from this click's
                // own blur, if the previously-focused field had an unsaved change, since blurring
                // always fires before the click handler that got us here runs. Without this, the
                // submission endpoint could read the database before a just-typed answer has
                // actually landed, rejecting the submission (or worse, accepting it with that
                // answer missing) even though the client believes everything was saved.
                await saveQueueRef.current.catch(() => undefined);
            } finally {
                setIsPreparingSubmit(false);
            }

            const response = await postSubmission({
                search: { email }
            });

            if (response && !isApiErrorResponse(response)) {
                setError(null);
                setRequestFailed(false);

                if (showSuccessToast) {
                    toast({
                        title: t('submitSuccess'),
                        description: t('submitSuccessMessage'),
                        status: 'success'
                    });
                }

                setQuestionnaire((prev) => {
                    if (!prev || !('pages' in prev)) {
                        return prev;
                    }

                    return {
                        ...prev,
                        questionnaireStatus: 'completed',
                        completed: new Date().toISOString()
                    };
                });
            } else {
                const description = getSubmitErrorDescription(response, t);
                // `response.detail` is always the same generic boilerplate for a validation
                // failure (see getSubmitErrorDescription) — normalize it to the same description
                // shown in the toast so the page's error banner (which reads `error.detail`)
                // doesn't show a different, less useful message than the toast did.
                setError(response ? { ...response, detail: description } : null);
                setRequestFailed(true);
                toast({
                    title: t('error'),
                    description,
                    status: 'error'
                });
            }

            return response;
        },
        [existingEmail, postSubmission, questionnairePublicId, setQuestionnaire, toast, t]
    );

    return {
        questionnaire,
        fetchQuestionnaire,
        loading,
        saveQuestionResponse,
        saving,
        submitQuestionnaire,
        submitting,
        requestFailed,
        error
    };
};

export default useQuestionnaire;
